import { createAsyncThunk } from "@reduxjs/toolkit";
import { API } from "lib/API";
import { handleThunkError, ThunkError } from "store/utils/thunk";
import {
  BeginAssetRFIDScanRequest,
  BeginAssetRFIDScanResponse,
  CreateStorageRequest,
  DataResponse,
  DuplicateStorageRequest,
  GetDeviceSoftwareVersionRequest,
  GetDeviceSoftwareVersionResponse,
  LockDeviceRequest,
  PagedDataResponse,
  Pagination,
  PaginationQueryParams,
  PowerOffDeviceRequest,
  RebootDeviceRequest,
  RefreshDeviceScreenRequest,
  RunSampleProximitySensorsRequest,
  RunSampleProximitySensorsResponse,
  StorageType,
  TStorage,
  UnlockDeviceRequest,
  UpdateDeviceSoftwareVersionRequest,
  UpdateDeviceSoftwareVersionResponse,
  UpdateStorageRequest,
  UUID,
} from "types";

type FetchStoragesParams = PaginationQueryParams & {
  search_term?: string;
  includeHeartbeat?: boolean;
  type?: StorageType;
  name?: string;
};

export const fetchStorages = createAsyncThunk(
  "storages/fetchStorages",
  async (params: FetchStoragesParams): Promise<{ results: TStorage[]; paging: Pagination }> => {
    const { data, metadata } = await API.GET<PagedDataResponse<TStorage[]>>("/storages", params);
    return { results: data, paging: metadata };
  },
);

export const fetchStorageById = createAsyncThunk("storages/fetchStorageById", async (id: string): Promise<TStorage> => {
  const { data } = await API.GET<DataResponse<TStorage>>(`/storages/${id}`);
  return data;
});

export const createStorage = createAsyncThunk(
  "storages/createStorage",
  async (request: CreateStorageRequest): Promise<TStorage> => {
    const { data } = await API.POST<CreateStorageRequest, DataResponse<TStorage>>("/storages", request);
    return data;
  },
);

export const duplicateStorage = createAsyncThunk(
  "storages/duplicateStorage",
  async (request: DuplicateStorageRequest): Promise<TStorage> => {
    const { data } = await API.POST<DuplicateStorageRequest, DataResponse<TStorage>>("/storages", request);
    return data;
  },
);

export const getDeviceSoftwareVersion = createAsyncThunk<
  GetDeviceSoftwareVersionResponse["data"],
  GetDeviceSoftwareVersionRequest,
  ThunkError
>("storages/remoteControl/getDeviceSoftwareVersion", async (request, { rejectWithValue }) => {
  try {
    const { data } = await API.POST<GetDeviceSoftwareVersionRequest, GetDeviceSoftwareVersionResponse>(
      `/storages/${request.storageId}/remote_controls/runtime_version`,
      request,
    );
    return data;
  } catch (err) {
    return handleThunkError(rejectWithValue, err);
  }
});

export const updateDeviceSoftwareVersion = createAsyncThunk<
  UpdateDeviceSoftwareVersionResponse["data"],
  UpdateDeviceSoftwareVersionRequest,
  ThunkError
>("storages/remoteControl/updateSoftwareVersion", async (request, { rejectWithValue }) => {
  try {
    const { data } = await API.POST<UpdateDeviceSoftwareVersionRequest, UpdateDeviceSoftwareVersionResponse>(
      `/storages/${request.storageId}/remote_controls/update_software`,
      request,
    );
    return data;
  } catch (err) {
    return handleThunkError(rejectWithValue, err);
  }
});

export const refreshDeviceScreen = createAsyncThunk<void, RefreshDeviceScreenRequest, ThunkError>(
  "storages/remoteControl/refreshDeviceScreen",
  async (request, { rejectWithValue }) => {
    try {
      await API.POST(`/storages/${request.storageId}/remote_controls/refresh_screen`, request);
    } catch (err) {
      return handleThunkError(rejectWithValue, err);
    }
  },
);

export const rebootDevice = createAsyncThunk<void, RebootDeviceRequest, ThunkError>(
  "storages/remoteControl/rebootDevice",
  async (request, { rejectWithValue }) => {
    try {
      await API.POST<RebootDeviceRequest, void>(
        `/storages/${request.storageId}/remote_controls/reboot_device`,
        request,
      );
    } catch (err) {
      return handleThunkError(rejectWithValue, err);
    }
  },
);

export const powerOffDevice = createAsyncThunk<void, PowerOffDeviceRequest, ThunkError>(
  "storages/remoteControl/powerOffDevice",
  async (request, { rejectWithValue }) => {
    try {
      await API.POST<PowerOffDeviceRequest, void>(
        `/storages/${request.storageId}/remote_controls/power_off_device`,
        request,
      );
    } catch (err) {
      return handleThunkError(rejectWithValue, err);
    }
  },
);

export const lockDevice = createAsyncThunk<void, LockDeviceRequest, ThunkError>(
  "storages/remoteControl/lockDevice",
  async (request, { rejectWithValue }) => {
    try {
      await API.POST<LockDeviceRequest, void>(`/storages/${request.storageId}/remote_controls/lock_device`, request);
    } catch (err) {
      return handleThunkError(rejectWithValue, err);
    }
  },
);

export const unlockDevice = createAsyncThunk<void, UnlockDeviceRequest, ThunkError>(
  "storages/remoteControl/unlockDevice",
  async (request, { rejectWithValue }) => {
    try {
      await API.POST<UnlockDeviceRequest, void>(
        `/storages/${request.storageId}/remote_controls/unlock_device`,
        request,
      );
    } catch (err) {
      return handleThunkError(rejectWithValue, err);
    }
  },
);

export const runDeviceRFIDTagsScan = createAsyncThunk<
  BeginAssetRFIDScanResponse["data"],
  BeginAssetRFIDScanRequest,
  ThunkError
>("storages/remoteControl/runDeviceRFIDTagsScan", async (request, { rejectWithValue }) => {
  try {
    const { data } = await API.POST(`/storages/${request.storageId}/remote_controls/scan_rfid_tags`, request);
    return data;
  } catch (err) {
    return handleThunkError(rejectWithValue, err);
  }
});

export const runSampleProximitySensors = createAsyncThunk<
  RunSampleProximitySensorsResponse["data"],
  RunSampleProximitySensorsRequest,
  ThunkError
>("storages/remoteControl/runSampleProximitySensors", async (request, { rejectWithValue }) => {
  try {
    const { data } = await API.POST(`/storages/${request.storageId}/remote_controls/sample_proximity_sensors`, request);
    return data;
  } catch (err) {
    return handleThunkError(rejectWithValue, err);
  }
});

export const createStorageViewer = createAsyncThunk(
  "storages/createStorageViewer",
  async (id: string): Promise<void> => {
    await API.POST<string, void>(`/storages/${id}/viewers`);
  },
);

export const updateStorage = createAsyncThunk(
  "storages/updateStorage",
  async (request: UpdateStorageRequest): Promise<TStorage> => {
    const { data } = await API.PUT<UpdateStorageRequest, DataResponse<TStorage>>(`/storages/${request.id}`, request);
    return data;
  },
);

export const deleteStorage = createAsyncThunk("storages/deleteStorage", async (id: UUID): Promise<void> => {
  await API.DELETE(`/storages/${id}`);
});

export const unlinkStorage = createAsyncThunk("storages/unlinkStorage", async (id: string): Promise<void> => {
  await API.POST<string, void>(`/storages/${id}/unlink`);
});

// How many access rules reference each drawer number of the storage's policy, so the portal can
// warn before a drawer that rules depend on is removed.
export const fetchStorageDrawerAccessCounts = createAsyncThunk(
  "storages/fetchStorageDrawerAccessCounts",
  async (id: string): Promise<Record<number, number>> => {
    const { data } = await API.GET<DataResponse<Record<number, number>>>(`/storages/${id}/drawer_access_counts`);
    return data;
  },
);
